import moment from "moment";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import {
  findContractType,
  findServiceContract,
} from "../../../../repositories/qerpapi/erp/relatorio/ContratosServicoRepository";
import { findClientsV3 } from "../../../../repositories/qerpapi/v3/ClienteRepository";
import { findCompaniesV3 } from "../../../../repositories/qerpapi/v3/EmpresaRepository";
import { notify } from "../../../../utils/notify";
import {
  contractPeriod,
  contractsColumns,
  groupment,
  initialContractFilter,
} from "../../constants";

const ContratoServicoContext = createContext();

const ContratoServicoContextProvider = ({ children }) => {
  const [filters, setFilters] = useState({ ...initialContractFilter });
  const [filterPeriod, setFilterPeriod] = useState({
    ...initialContractFilter.searchDate,
  });

  const [columns, setColumns] = useState(contractsColumns);

  const [isMainFetchEnabled, setMainFetchEnabled] = useState(false);
  const [isGrouped, setGrouped] = useState(false);

  const [cachedCompanies, setCachedCompanies] = useState({});
  const [cachedClients, setCachedClients] = useState([]);
  const [cachedServiceTypes, setCachedServiceTypes] = useState({});
  const [cachedContracts, setCachedContracts] = useState({
    data: [],
    period: {},
  });

  // ----------------------------------------------------------------------------

  const setContractData = useCallback(
    (data) => setCachedContracts((prev) => ({ ...prev, ...data })),
    []
  );

  // ----------------------------------------------------------------------------

  const setMultipleSort = useCallback((sortValues) => {
    setFilters((prev) => ({ ...prev, multipleSort: sortValues }));
  }, []);

  // ----------------------------------------------------------------------------

  const handleSetSort = useCallback(
    (sorted) => {
      const sortedColumns = JSON.parse(filters.multipleSort);
      const sortedIndex = sortedColumns.findIndex(
        (value) => sorted.column === value.column
      );

      if (sorted.direction === "disabled" && sortedIndex >= 0) {
        sortedColumns.splice(sortedIndex, 1);
        return setMultipleSort(JSON.stringify(sortedColumns));
      }

      if (sortedIndex >= 0) {
        setMultipleSort(JSON.stringify(sortedColumns));
        sortedColumns[sortedIndex] = sorted;
        return setMultipleSort(JSON.stringify(sortedColumns));
      }

      sortedColumns.push(sorted);

      setMultipleSort(JSON.stringify(sortedColumns));
    },
    [filters.multipleSort, setMultipleSort]
  );

  const handleSort = useCallback(
    (sorted) => {
      const sortedColumns = columns.map((column) => {
        if (column.id === sorted.id) {
          if (column.sorting === "desc") {
            handleSetSort({ column: column.id, direction: "disabled" });
            return {
              ...column,
              sorting: "disabled",
            };
          }

          if (column.sorting === "disabled") {
            handleSetSort({ column: column.id, direction: "asc" });
            return {
              ...column,
              sorting: "asc",
            };
          }

          if (column.sorting === "asc") {
            handleSetSort({ column: column.id, direction: "desc" });
            return {
              ...column,
              sorting: "desc",
            };
          }
        }
        return column;
      });

      setColumns(sortedColumns);
    },
    [columns, handleSetSort]
  );

  // ----------------------------------------------------------------------------

  const setSelectedCompany = useCallback(
    (selectedCompany) =>
      setFilters((prev) => ({ ...prev, multicompany: selectedCompany })),
    []
  );

  const setSelectedCustomer = useCallback(
    (selectedCustomer) =>
      setFilters((prev) => ({ ...prev, customer: selectedCustomer })),
    []
  );

  const setSelectedServiceType = useCallback(
    (newServiceType) =>
      setFilters((prev) => ({ ...prev, type: newServiceType })),
    []
  );

  const setCustomDatePeriod = useCallback((newValue) => {
    setFilterPeriod((prev) => {
      return {
        ...prev,
        ...newValue,
      };
    });
  }, []);

  const setPeriod = useCallback(
    (newValue) => {
      if (newValue === contractPeriod.Personalizado) {
        setCustomDatePeriod({
          startDate: undefined,
          endDate: undefined,
        });
      }

      setFilters((prev) => ({
        ...prev,
        period: newValue,
      }));
    },
    [setCustomDatePeriod]
  );

  const setCustomSearchDate = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      startDate: newValue?.startDate
        ? moment(newValue?.startDate).format("YYYY-MM-DD")
        : moment().startOf("month").format("YYYY-MM-DD"),
      endDate: newValue?.endDate
        ? moment(newValue?.endDate).format("YYYY-MM-DD")
        : moment().endOf("month").format("YYYY-MM-DD"),
    }));
  }, []);

  const setMulticompany = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, multicompany: newValue }));
  }, []);

  const setContractNumber = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, number: newValue }));
  }, []);

  const setStatus = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, status: newValue }));
  }, []);

  const setDateFilter = useCallback(
    (newValue) => {
      setFilters((prev) => ({ ...prev, dateFilter: newValue }));
      const switchDateDisabled = {
        emissionDate: "effectiveDate",
        effectiveDate: "emissionDate",
      };
      const sortedColumns = columns.map((column) => {
        if (switchDateDisabled[newValue] === column.id) {
          handleSetSort({ column: column.id, direction: "disabled" });
          return {
            ...column,
            sorting: "disabled",
          };
        } else if (newValue === column.id) {
          handleSetSort({ column: column.id, direction: "asc" });
          return {
            ...column,
            sorting: "asc",
          };
        }
        return column;
      });

      setColumns(sortedColumns);
    },
    [columns, handleSetSort]
  );

  const setGroupBy = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, group: newValue }));
  }, []);

  const setModality = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, modality: newValue }));
  }, []);

  // ----------------------------------------------------------------------------

  const applyPeriod = useCallback(
    (periodDates) => {
      setCustomSearchDate(periodDates);
      setContractData({ period: periodDates });
      setCustomDatePeriod({
        startDate: periodDates?.startDate
          ? moment(periodDates.startDate, "YYYY-MM-DD").toDate()
          : moment().startOf("month").toDate(),
        endDate: periodDates?.endDate
          ? moment(periodDates.endDate, "YYYY-MM-DD").toDate()
          : moment().endOf("month").toDate(),
      });
    },
    [setContractData, setCustomDatePeriod, setCustomSearchDate]
  );

  useEffect(() => {
    setCustomSearchDate(filterPeriod);
  }, [filterPeriod, setCustomSearchDate]);

  // ----------------------------------------------------------------------------

  const handleMainData = useCallback(
    async (res) => {
      const data = res?.data;
      if (!res?.success) {
        if (res.statusCode === 401) {
          notify("Token expirado", true, "error");
        }
        notify(res.message, true, "error");
        return;
      }
      setGrouped(filters.group !== groupment["Sem agrupamento"]);
      setContractData({
        data: data.movements ?? [],
      });
      applyPeriod(data?.searchPeriod);
    },
    [applyPeriod, filters.group, setContractData]
  );

  const { isLoading, isRefetching, refetch } = useQuery(
    "contrato-servico",
    () => findServiceContract(filters),
    {
      enabled: !!sessionStorage.getItem("gfAccessToken") && isMainFetchEnabled,
      onSuccess: handleMainData,
    }
  );

  // ----------------------------------------------------------------------------

  const validateIfCompanyIdExistAndSetNewDefault = useCallback(
    async (userCompanies) => {
      const companiesIds = Object.values(userCompanies);

      if (!companiesIds.includes(filters.multicompany)) {
        setSelectedCompany(userCompanies.Todos);
      }
    },
    [filters.multicompany, setSelectedCompany]
  );

  const handleCompaniesData = useCallback(
    async (companies) => {
      const data = companies?.data ?? [];

      if (!companies.success) notify(companies.message, true, "error");

      if (companies?.data) {
        const allCompaniesString = data.map((company) => company.id).join(",");
        const formatedCompanies = {
          Todos: allCompaniesString,
        };

        data.forEach(
          (company) => (formatedCompanies[company.nomeempresa] = company.id)
        );

        await validateIfCompanyIdExistAndSetNewDefault(formatedCompanies);

        setCachedCompanies(formatedCompanies);
        setMainFetchEnabled(true);
      }
    },
    [validateIfCompanyIdExistAndSetNewDefault]
  );

  const { isLoading: isCompanyLoading } = useQuery(
    "empresas",
    findCompaniesV3,
    {
      enabled: !!sessionStorage.getItem("gfAccessToken"),
      onSuccess: handleCompaniesData,
    }
  );

  // ----------------------------------------------------------------------------

  const handleClientsData = useCallback(async (clients) => {
    const data = clients?.data ?? [];

    if (!clients.success) return notify(clients.message, true, "error");

    const formatedClients = data.map((client) => ({
      description: client.razao,
      value: client.id,
    }));

    setCachedClients(formatedClients);
  }, []);

  const { isLoading: isClientLoading } = useQuery("clientes", findClientsV3, {
    enabled: !!sessionStorage.getItem("gfAccessToken"),
    onSuccess: handleClientsData,
  });

  // ----------------------------------------------------------------------------

  const handleServiceTypeData = useCallback(async (res) => {
    const data = res?.data ?? [];

    if (!res.success) notify(res.message, true, "error");

    if (res?.data) {
      const serviceTypes = {};

      data.forEach(
        (serviceType) => (serviceTypes[serviceType.descricao] = serviceType.id)
      );

      setCachedServiceTypes(serviceTypes);
    }
  }, []);

  const { isLoading: isServiceTypeLoading } = useQuery(
    "tipo-servico",
    findContractType,
    {
      enabled:
        !!sessionStorage.getItem("gfAccessToken") &&
        !!sessionStorage.getItem("empresa_cnpj"),
      onSuccess: handleServiceTypeData,
    }
  );

  // ----------------------------------------------------------------------------

  const contextValue = useMemo(
    () => ({
      filters,
      filterPeriod,
      columns,

      setSelectedCompany,
      setSelectedCustomer,
      setPeriod,
      setMulticompany,
      setContractNumber,
      setStatus,
      setDateFilter,
      setGroupBy,
      setMultipleSort,

      setCustomDatePeriod,
      setModality,
      setSelectedServiceType,

      handleSort,

      cachedContracts,
      cachedCompanies,
      cachedClients,
      cachedServiceTypes,

      isGrouped,
      isLoading,
      isRefetching,
      isCompanyLoading,
      isClientLoading,
      isServiceTypeLoading,

      refetch,
    }),
    [
      filters,
      filterPeriod,
      columns,
      setSelectedCompany,
      setSelectedCustomer,
      setPeriod,
      setMulticompany,
      setContractNumber,
      setStatus,
      setDateFilter,
      setGroupBy,
      setMultipleSort,

      setCustomDatePeriod,
      setModality,
      setSelectedServiceType,
      handleSort,
      cachedContracts,
      cachedCompanies,
      cachedClients,
      cachedServiceTypes,
      isGrouped,
      isLoading,
      isRefetching,
      isCompanyLoading,
      isClientLoading,
      isServiceTypeLoading,
      refetch,
    ]
  );

  return (
    <ContratoServicoContext.Provider value={contextValue}>
      {children}
    </ContratoServicoContext.Provider>
  );
};

const useContratoServicoService = () => {
  const context = useContext(ContratoServicoContext);

  if (!context)
    throw new Error(
      "useContratoServicoService must be used with ContratoServicoContextProvider"
    );

  return context;
};

export { ContratoServicoContextProvider, useContratoServicoService };
